import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@components/Button";
import { Modal } from "@components/Modal/Modal";
import { Progress } from "@components/Progress/Progress";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useConsentLedgerStatusQuery } from "@services/budget/queries";
import {
  ConsentLedgerConsolidatedStatus,
  ConsentLedgerConsolidatedStatusStepProgress,
} from "@services/budget/types";
import { queryClient } from "@/queryClient";

interface IBudgetLedgerStatusProgressModalProps extends IUseModalProps {
  ledgerConsentId: number;
}

export const BudgetLedgerStatusProgressModal: React.FC<
  IBudgetLedgerStatusProgressModalProps
> = (props) => {
  const { ledgerConsentId, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const { data: ledgerStatusData } = useConsentLedgerStatusQuery(
    ledgerConsentId,
    true,
  );

  const status =
    ledgerStatusData?.consolidatedState ??
    ConsentLedgerConsolidatedStatus.Pending;

  const targetPercent = useMemo(
    () => ConsentLedgerConsolidatedStatusStepProgress[status],
    [status],
  );

  const [animatedPercent, setAnimatedPercent] = useState(0);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setAnimatedPercent(targetPercent);
    });

    return () => cancelAnimationFrame(id);
  }, [targetPercent]);

  useEffect(() => {
    if (targetPercent === 100) {
      const id = setTimeout(() => {
        void queryClient.refetchQueries({ queryKey: ["budget", "metadata"] });
        modal.close();
      }, 900);

      return () => clearTimeout(id);
    }
  }, [targetPercent, modal]);

  const animationDuration = useMemo(() => {
    switch (status) {
      case ConsentLedgerConsolidatedStatus.Pending:
        return "duration-[25s]";
      case ConsentLedgerConsolidatedStatus.Processing:
        return "duration-[8s]";
      default:
        return "duration-800";
    }
  }, [status]);

  return (
    <Modal
      modal={modal}
      containerClassName="pt-0"
      actionButtons={
        <div className="flex w-full justify-center">
          <Button className="px-5 py-3" onClick={modal.close}>
            {t("budgetLedgerStatusProgressModal.close")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center text-center">
        <img className="size-40" src="/images/budget/empty-card.webp" />
        <h2 className="text-lg">
          {t("budgetLedgerStatusProgressModal.title")}
        </h2>
        <p className="text-sm text-grey-dark">
          {t("budgetLedgerStatusProgressModal.description")}
        </p>
        <Progress
          color="green"
          percent={animatedPercent}
          className="mt-3.5"
          progressClassName={twMerge("ease-linear", animationDuration)}
        />
      </div>
    </Modal>
  );
};
